import { Box, Column, Expanded, Row, Text, Alignment } from '@st/document-rendering'
import { CalculatorTapeAnnotation, CalculatorTapeLineItem } from '@st/sdk'
import { memo, ReactNode } from 'react'
import { CALCULATOR_TAPE_LAYOUT } from './calculator-tape-model'

// ---------------------------------------------------------------------------
// Public component
// ---------------------------------------------------------------------------
export const CalculatorTapeView = memo(CalculatorTapeViewBase)

type Props = { annotation: CalculatorTapeAnnotation }

function CalculatorTapeViewBase({ annotation }: Props) {
  return (
    <CalculatorTapeScaffold
      width={annotation.bounds.width}
      height={annotation.bounds.height}
      title={annotation.title ?? ''}
      lineItems={annotation.lineItems}
      total={annotation.total}
      renderRow={(item, idx) => (
        <LineItemRow key={idx} description={item.description ?? ''} amount={item.amount} />
      )}
    />
  )
}

function CalculatorTapeScaffold({
  width,
  height,
  title,
  lineItems,
  total,
  renderRow,
  border = true
}: {
  width: number
  height: number
  title: string
  lineItems: CalculatorTapeLineItem[]
  total: string
  renderRow: (item: CalculatorTapeLineItem, index: number) => ReactNode
  border?: boolean
}) {
  const layout = CALCULATOR_TAPE_LAYOUT

  return (
    <Box
      width={width}
      height={height}
      padding={layout.container.padding}
      border={border ? { color: '#000000', width: 2 } : undefined}
      backgroundColor="#FFFFFF"
    >
      <Column>
        {/* Title */}
        <Box height={layout.title.height} alignment="centerLeft">
          <Text fontSize={layout.title.fontSize} fontFamily="Helvetica">
            {title}
          </Text>
        </Box>

        {/* Spacer before first divider */}
        <Box height={layout.dividerHeight} />

        {/* Line items */}
        {lineItems.map((li, idx) => renderRow(li, idx))}

        {/* Divider before total */}
        <HDivider height={layout.dividerHeight} />

        {/* Total row */}
        <TotalRow total={total} />
      </Column>
    </Box>
  )
}

function LineItemRow({ description, amount }: { description: string; amount: string }) {
  const layout = CALCULATOR_TAPE_LAYOUT
  const isNegative = parseFloat(amount) < 0

  return (
    <Box height={layout.row.height}>
      <Row gap={layout.column.gap} crossAxisAlignment="center">
        <Expanded flex={layout.column.leftFlex}>
          <Text fontSize={layout.row.fontSize} fontFamily="Helvetica">
            {description}
          </Text>
        </Expanded>
        <Expanded flex={layout.column.rightFlex}>
          <Text
            fontSize={layout.row.fontSize}
            fontFamily="Helvetica"
            textAlign="right"
            color={isNegative ? '#CB3A31' : '#000000'}
          >
            {amount}
          </Text>
        </Expanded>
      </Row>
    </Box>
  )
}

function TotalRow({ total }: { total: string }) {
  const layout = CALCULATOR_TAPE_LAYOUT
  return (
    <Box height={layout.row.height}>
      <Row crossAxisAlignment="center">
        <Expanded flex={layout.column.leftFlex}>
          <Text fontSize={layout.row.fontSize} fontFamily="Helvetica" fontWeight="bold">
            Total
          </Text>
        </Expanded>
        <Expanded flex={layout.column.rightFlex}>
          <Text
            fontSize={layout.row.fontSize}
            fontFamily="Helvetica"
            fontWeight="bold"
            textAlign="right"
          >
            {total}
          </Text>
        </Expanded>
      </Row>
    </Box>
  )
}

function HDivider({ height }: { height: number }) {
  return (
    <Column crossAxisAlignment="stretch" mainAxisSize="min">
      <Box height={height / 2 - 0.5} />
      <Box height={1} backgroundColor="#000000" />
      <Box height={height / 2 - 0.5} />
    </Column>
  )
}
